import { Clock, Settings } from 'lucide-react';
import { useSubscription } from '@/store/subscription';
import { useWorkspaceStore } from '@/store/workspace';
import { Button } from '@/components/ui/button';

interface TrialBannerProps {
  onManageClick?: () => void;
}

export function TrialBanner({ onManageClick }: TrialBannerProps) {
  const { isTrialing, trialDaysRemaining, createPortalSession } = useSubscription();
  const workspaceName = useWorkspaceStore(s => s.currentWorkspace?.name);

  // Only show during trial
  if (!isTrialing) return null;

  const handleManageClick = async () => {
    if (onManageClick) {
      onManageClick();
      return;
    }

    const url = await createPortalSession();
    if (url) {
      window.location.href = url;
    }
  };

  // Determine banner style based on days remaining
  const isUrgent = trialDaysRemaining <= 3;
  const bannerClasses = isUrgent
    ? 'bg-amber-50 border-amber-200 text-amber-900'
    : 'bg-blue-50 border-blue-200 text-blue-900';

  const iconClasses = isUrgent ? 'text-amber-600' : 'text-blue-600';

  return (
    <div className={`px-4 py-2 border-b flex items-center justify-between ${bannerClasses}`}>
      <div className="flex items-center gap-2">
        <Clock className={`h-4 w-4 ${iconClasses}`} />
        <span className="text-sm font-medium">
          {workspaceName ? `${workspaceName}: ` : ''}
          {trialDaysRemaining === 1
            ? '1 day left in trial'
            : `${trialDaysRemaining} days left in trial`}
        </span>
        <span className="text-sm">
          &mdash; then $2.99/month
        </span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleManageClick}
        className="h-7 text-xs hover:bg-blue-100"
      >
        <Settings className="h-3 w-3 mr-1" />
        Manage
      </Button>
    </div>
  );
}
